import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';

import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import BannerVideo from './components/banner-video';
import Modal from './components/modal';
import Form from './components/form';

import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';

import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalDefault from './components/modal-default';
import ModalVideoPopup from './components/modal-video-popup';
import EqualHeight from './util/equal-height';

import LogosSlider from './components/logos-silder';

import TestimonialsSlider from './components/testimonials-slider';
import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import SideTabs from './components/side-tabs';
import NewsletterSubmit from './components/newsletter-submit';
import MediaContentSlider from './components/media-content-slider';
import ImageGallerySlider from './components/image-gallery-slider';

import AccordionImage from './blocks/accordion-image';

import ResourceTemplate from './templates/resource';
import EventTemplate from './templates/event';
import ProjectTemplate from './templates/project';
import ParentStateWatcher from "./blocks/posts";
import BannerSlider from "./components/banner-slider";


class App {
	constructor() {
		this.instances = {
			components: {
				accordion: [],
				bannerVideo: null,
				equalHeight: [],
				imageGallerySlider: [],
				bannerSlider: [],
				logosSlider: [],
				mediaContentSlider: [],
				modal: [],
				form: [],
				modalVideoPopup: [],
				newsletterSubmit: [],
				postsListBlog: [],
				postsListSearch: [],
				siteHeader: null,
				socialShare: null,
				statsSlider: [],
				testimonialsSlider: [],
				themeWelcome: null
			},
			templates: {
				resourceTemplate: null,
				eventTemplate: null,
				projectTemplate: null
			},
			blocks: {
				accordionImage: [],
				sideTabs: []
			},
			scrollWatcher: null,
			imageBuddy: null
		};

		this.init();
		this.initComponents();
		this.initTemplates();
		this.initBlocks();
		this.initSocialShare();
	}

	init() {
		processExternalLinks({
			target: '_blank',
			rel: 'noopener'
		});

		Modal.setDefaults({
			closeDuration: 400,
			closeButtonContent: `
				<svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`.trim()
		});

		this.scrollWatcher = new ScrollWatcher((params) => {
			ScrollWatcher.defaultCallback(params, 100);
		});

		// initialize ImageBuddy and setup events
		this.instances.imageBuddy = new ImageBuddy({
			lazyLoad: true
			// debug: true
		});

		ImageBuddy.on('image-loaded', (imgEl) => {
			objectFitImages(imgEl);
		});

		SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
			this.instances.imageBuddy.update(opts || {});
		});

		initHelloBar((hbEl) => {
			document.body.style.marginTop = `${hbEl.offsetHeight}px`;

			const siteHeaderEl = document.querySelector('.site-header');

			if (siteHeaderEl) {
				siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
			}
		});

		// ObjectFitImages: only act upon images that have a valid src attribute
		// Note: use the 'object-fit-polyfill' SCSS mixin to create the necessary 'font-family' attribute
		objectFitImages('img:not([src^="data:"])');
	}

	async initComponents() {
		// theme welcome
		// TODO: remove during development
		const themeWelcomeEl = document.querySelector('.theme-welcome');

		if (themeWelcomeEl) {
			this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
		}

		// site header
		const siteHeaderEl = document.querySelector('.site-header');

		if (siteHeaderEl) {
			this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
		}

		// newsletter signup
		Array.from(document.querySelectorAll('.newsletter-signup')).forEach((newsletterSignupEl) => {
			this.instances.components.newsletterSubmit.push(new NewsletterSubmit(newsletterSignupEl));
		});

		// banner cover video
		const bannerCoverVideoEl = document.querySelector('.banner__cover-video');

		if (bannerCoverVideoEl) {
			this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
		}

		// posts list - blog
		Array.from(document.querySelectorAll('.posts-list--blog')).forEach((postsListBlogEl) => {
			this.instances.components.postsListBlog.push(new PostsListBlog(postsListBlogEl));
		});

		// posts list - search
		Array.from(document.querySelectorAll('.posts-list--search')).forEach(
			(postsListSearchEl) => {
				this.instances.components.postsListSearch.push(
					new PostsListSearch(postsListSearchEl)
				);
			}
		);

		// Default Modal
		Array.from(document.querySelectorAll('.js__modal--default')).forEach(
			(modalDefaultEl) => {
				this.instances.components.modal.push(new ModalDefault(modalDefaultEl));
			}
		);

		// Modal Video Popup
		Array.from(document.querySelectorAll('.js__modal--video')).forEach(
			(modalVideoPopupEl) => {
				this.instances.components.modalVideoPopup.push(new ModalVideoPopup(modalVideoPopupEl));
			}
		);

		// Testimonials Slider
		Array.from(document.querySelectorAll('.block-testimonials')).forEach(

			(testimonialsBlock) => {
				this.slider = testimonialsBlock.querySelector('.block-testimonials__testimonials-slider');
				this.prevButton = testimonialsBlock.querySelector('.custom-prev-button');
				this.nextButton = testimonialsBlock.querySelector('.custom-next-button');
				if (this.slider) {
					this.instances.components.testimonialsSlider.push(
						new TestimonialsSlider(this.slider, this.prevButton, this.nextButton)
					);
				}
			}
		);

		// Banner Slider
		Array.from(document.querySelectorAll('.banner-default__image-col')).forEach(

			(BannerSliderComponent) => {
				this.slider = BannerSliderComponent.querySelector('.banner-default__image-wrapper--multiple');
				this.prevButton = BannerSliderComponent.querySelector('.custom-prev-button');
				this.nextButton = BannerSliderComponent.querySelector('.custom-next-button');
				this.nav = BannerSliderComponent.querySelector('.custom-nav-banner-slider');
				if (this.slider) {
					this.instances.components.bannerSlider.push(
						new BannerSlider(this.slider, this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);
		// Featured Projects Slider (reuses BannerSlider)
		Array.from(document.querySelectorAll('.block-posts__featured-post-slider-wrapper')).forEach((sliderWrapper) => {
			const slider = sliderWrapper.querySelector('.banner-default__image-wrapper--multiple');
			const prevButton = sliderWrapper.closest('.block-posts__featured-post-image').querySelector('.custom-prev-button');
			const nextButton = sliderWrapper.closest('.block-posts__featured-post-image').querySelector('.custom-next-button');
			const nav = null;

			if (slider) {
				this.instances.components.bannerSlider.push(
					new BannerSlider(slider, prevButton, nextButton, nav, false)
				);
			}
		});

		// Image Gallery Slider
		Array.from(document.querySelectorAll('.block-image-gallery')).forEach(

			(imageGalleryBlock) => {
				this.slider = imageGalleryBlock.querySelector('.block-image-gallery__slider');
				this.prevButton = imageGalleryBlock.querySelector('.custom-prev-button');
				this.nextButton = imageGalleryBlock.querySelector('.custom-next-button');
				this.nav = imageGalleryBlock.querySelector('.custom-nav-container-image-gallery');
				if (this.slider) {
					this.instances.components.imageGallerySlider.push(
						new ImageGallerySlider(this.slider, this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);

		Array.from(document.querySelectorAll('.block-form')).forEach((formEl) => {
			this.instances.components.form.push(new Form(formEl));
		});

		Array.from(document.querySelectorAll('.js-banner-form')).forEach((formEl) => {
			this.instances.components.form.push(new Form(formEl));
		});

		// Logos Slider
		Array.from(document.querySelectorAll('.block-logos')).forEach(

			(logosBlock) => {
				this.slider = logosBlock.querySelector('.block-logos__logos-slider');
				this.nav = logosBlock.querySelector('.custom-nav-container-logos');
				if (this.slider) {
					this.instances.components.logosSlider.push(
						new LogosSlider(this.slider, this.nav)
					);
				}
			}
		);

		// Media Content Slider
		Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach(

			(mediaContentSlider) => {
				this.slider = mediaContentSlider.querySelector('.block-media-content__slider');
				this.instances.components.mediaContentSlider.push(
					new MediaContentSlider(this.slider)
				);
			}
		);

		// Social Share
		const socialShareEl = document.querySelector('.tmpl-single');

		if (socialShareEl) {
			this.instances.components.socialShare = new SocialShare(socialShareEl);
		}

		// accordion
		Array.from(document.querySelectorAll('.accordion-container')).forEach((accordionEl) => {
			this.instances.components.accordion.push(new Accordion(accordionEl));
		});

		// Stats Slider
		Array.from(document.querySelectorAll('.block-stats')).forEach(
			(statsBlock) => {
				this.slider = statsBlock.querySelector('.stats__stat-slider');
				this.nav = statsBlock.querySelector('.custom-nav-container-stats');
				if (this.slider) {
					this.instances.components.statsSlider.push(
						new StatsSlider(this.slider, this.nav)
					);
				}
			}
		);

		// Equalize Height
		Array.from(document.querySelectorAll('.equal-height')).forEach(

			(equalHeightContainer) => {

				this.instances.components.equalHeight.push(
					new EqualHeight(equalHeightContainer)
				);
			}
		);

	}

	initTemplates() {

		// banner cover video
		const bannerCoverVideoEl = document.querySelector('.banner-default__background-video');

		if (bannerCoverVideoEl) {
			this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
		}

		const eventTemplateEl = document.querySelector('.post-type-archive-event');

		if (eventTemplateEl) {
			this.instances.templates.eventTemplate = new EventTemplate(eventTemplateEl);
		}

		const projectTemplateEl = document.querySelector('.post-type-archive-project, .tmpl-project');

		if (projectTemplateEl) {
			this.instances.templates.projectTemplate = new ProjectTemplate(projectTemplateEl);
		}

	}

	async initBlocks() {

		// Accordion Image
		Array.from(document.querySelectorAll('.block-accordion')).forEach(
			(accordionImage) => {
				this.instances.blocks.accordionImage.push(
					new AccordionImage(accordionImage)
				);
			}
		);

		Array.from(document.querySelectorAll('section.block-posts')).forEach(section => {
			new ParentStateWatcher(section, '.block-inner--featured-plus', 'block-posts--featured-plus');
		});

		// Side Tabs
		Array.from(document.querySelectorAll('.block-side-tabs')).forEach(
			(sideTabs) => {
				this.instances.blocks.sideTabs.push(
					new SideTabs(sideTabs)
				);
			}
		);

		// setup block animation watchers
		const animateBlocks = document.querySelectorAll('.block-animate');

		if (animateBlocks && animateBlocks.length) {
			try {
				await loadIntersectionObserver();

				animateBlocks.forEach((watchEl) =>
					createSingleUseObserver(watchEl, (entry, el) => {
						el.classList.remove('block-animate');
					})
				);
			} catch (err) {
				console.error(err);
			}
		}
	}

	initSocialShare() {
		const socialShareEls = document.querySelectorAll('[data-social-share]');

		Array.from(socialShareEls).forEach((el) => {
			el.addEventListener('click', (e) => {
				const site = el.getAttribute('data-social-share');
				const shareUrl = el.getAttribute('href');

				e.preventDefault();

				if (!shareUrl) {
					return;
				}

				window.open(shareUrl, `${site}Share`, 'width=626,height=436');
			});
		});
	}
}

export default App;
